/**
 * D194: an instrument applied to a thing — `use X on Y`, `open Y with X` —
 * and the one case where that is a door's opening rather than a `use`.
 *
 * @remarks
 * Split from `narrow-parsed-command.ts` at the file-size cap, the way
 * `narrow-intercepts.ts` was; it reads the door helpers back from there.
 *
 * @packageDocumentation
 */

import { isUsedAlone, type ItemId, type RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandNarrowingOutcome } from './runtime-action.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { conditionNamesItem } from '../conditions/condition-names-item.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { applicableDoorRefusal, canOpenByHand, narrowed, rejected } from './narrow-parsed-command.js';

/**
 * D194: an instrument applied to a thing — `use X on Y`, `open Y with X`.
 *
 * @remarks
 * The pack's own word comes first: an authored interaction between the pair
 * (either way round — R33) is what the player asked for. Failing that, if Y
 * is a door whose `handOpenCondition` NAMES X (`has_item X`) and hands are
 * enough right now, this is Y's opening spoken from X's side — the same
 * `open_container` action `open Y` narrows to, so the door's refusal, its
 * already-open answer and its opening lines all come from one place. Before
 * this, `use the key on the chest` fell through to "those two have nothing to
 * say to each other" while `open the chest` turned the key: two paths
 * resolving one act and disagreeing about the one pair in the pack that most
 * obviously has something to say (content-standards §2.2; Mike, 2026-09-18,
 * on the Salt Debt).
 *
 * The door's own `openRefusal` still speaks first when it applies (D135) —
 * a refusal that names the key is the fiction's, and the key in hand does
 * not override it.
 *
 * @param instrumentItemId - The thing applied
 * @param targetItemId - The thing it is applied to
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @returns The door's opening, or the pair's `use`
 */
export const narrowInstrumentOnTarget = (
	instrumentItemId: ItemId,
	targetItemId: ItemId,
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
): CommandNarrowingOutcome => {
	const target = requireItem(bundle, targetItemId);

	if (
		!hasAuthoredInteraction(bundle, instrumentItemId, targetItemId) &&
		target.openable?.handOpenCondition !== undefined &&
		conditionNamesItem(target.openable.handOpenCondition, instrumentItemId) &&
		canOpenByHand(target, bundle, state)
	) {
		const openRefusal = applicableDoorRefusal(target.openRefusal, bundle, state);

		return openRefusal === undefined
			? narrowed({ itemId: targetItemId, type: 'open_container' })
			: rejected(bundle, state, { itemId: targetItemId, type: 'open_refused' }, openRefusal.narratives);
	}

	return narrowed({ instrumentItemId, targetItemId, type: 'use' });
};

/**
 * D269: the thing a player means to open a lock WITH when they name only the lock — the one thing they carry whose
 * authored interaction with it, either way round (R33), would OPEN it right now: an arm whose conditions hold and whose
 * effects open that very thing. Only an opening counts. An arm that does something else to the pair (the suit filling
 * at the manifold, wax sealing a jar) is that pair's act when the player names it, and a refusal the pack wrote for a
 * pair (the band at the guard: "not authorized") is that pair's answer; neither is a guess at what "open" meant. None,
 * or more than one, and the lock's own refusal stands. Without a state (tooling) nobody carries anything.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @param targetItemId - The lock named
 * @returns The one carried instrument that opens it, or `undefined`
 */
export const impliedInstrumentOf = (bundle: EngineGameBundle, state: RuntimeState | undefined, targetItemId: ItemId): ItemId | undefined => {
	if (state === undefined) {
		return undefined;
	}

	const candidates = state.world.inventoryItemIds.filter((itemId) => itemId !== targetItemId && wouldOpen(bundle, state, itemId, targetItemId));

	return candidates.length === 1 ? candidates[0] : undefined;
};

/**
 * D269: would one of the pair's arms, either way round, open the lock now — hold, and open it?
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param instrumentItemId - The carried thing
 * @param lockItemId - The lock
 * @returns Whether an arm between them holds and opens the lock
 */
const wouldOpen = (bundle: EngineGameBundle, state: RuntimeState, instrumentItemId: ItemId, lockItemId: ItemId): boolean =>
	[
		...requireItem(bundle, instrumentItemId).useInteractions.filter((interaction) => interaction.targetItemId === lockItemId),
		...requireItem(bundle, lockItemId).useInteractions.filter((interaction) => interaction.targetItemId === instrumentItemId),
	].some(
		(interaction) =>
			interaction.effects.some((effect) => effect.type === 'open_container' && effect.itemId === lockItemId) &&
			interaction.conditions.every((condition) => evaluateCondition(condition, bundle, state)),
	);

/**
 * Does the pack author an interaction between these two, either way round
 * (R33's symmetric lookup)? Bundle data only.
 *
 * @param bundle - The loaded bundle
 * @param first - One of the pair
 * @param second - The other
 * @returns Whether an authored interaction joins them
 */
const hasAuthoredInteraction = (bundle: EngineGameBundle, first: ItemId, second: ItemId): boolean =>
	requireItem(bundle, first).useInteractions.some((interaction) => interaction.targetItemId === second) ||
	requireItem(bundle, second).useInteractions.some((interaction) => interaction.targetItemId === first);

/**
 * D245: does this thing, used on its own, answer right now — does one of its
 * target-free arms hold? The handler reads the same arms against the same
 * state and finds the same one. Without a state (tooling) only an arm with no
 * conditions can be known to hold.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @param instrumentItemId - The thing the player used
 * @returns Whether a bare `use` reaches an authored arm
 */
export const answersUsedAlone = (bundle: EngineGameBundle, state: RuntimeState | undefined, instrumentItemId: ItemId): boolean =>
	requireItem(bundle, instrumentItemId).useInteractions.some(
		(interaction) =>
			isUsedAlone(interaction) && interaction.conditions.every((condition) => state !== undefined && evaluateCondition(condition, bundle, state)),
	);
